import { api, apiErrorMessage, useAuthStore } from '@bemor/web-kit';
import {
    Badge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateField,
    DateTime,
    Field,
    FormSheet,
    Input,
    PageHeader,
    RowActions,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    Textarea,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { RiskList, RiskSharedList } from '@/api/types';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';

const PATH = '/staff/risk/lists';
const KINDS = [
    'country',
    'region',
    'timezone',
    'domain',
    'email',
    'payment_account',
    'ip',
    'device',
    'cidr',
    'text',
] as const;

type ListDetail = RiskList & {
    entries?: {
        id: number;
        value: string;
        note: string | null;
        added_by: string;
        added_at: string;
        expires_at: string | null;
        expired: boolean;
    }[];
};

function NewListSheet({ onClose }: { onClose: () => void }) {
    const [name, setName] = useState('');
    const [kind, setKind] = useState<string>('email');
    const [description, setDescription] = useState('');
    const [error, setError] = useState<unknown>(null);
    const write = useWrite(
        async () =>
            (await api().post<RiskList>(PATH, { name, kind, description }))
                .data,
        [PATH],
    );
    return (
        <FormSheet
            open
            onOpenChange={(o) => (o ? null : onClose())}
            size="sm"
            title="New list"
            submitLabel="Create list"
            busy={write.isPending}
            submitDisabled={name.trim() === ''}
            dirty
            error={error}
            data-testid="risk-list-form"
            onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                write.mutate(undefined, {
                    onSuccess: onClose,
                    onError: setError,
                });
            }}
        >
            <Field
                id="list-name"
                label="Name"
                hint="lower_case, digits and underscores; rules name it with in_list"
            >
                <Input
                    id="list-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </Field>
            <Field id="list-kind" label="Kind">
                <Select value={kind} onValueChange={setKind}>
                    <SelectTrigger id="list-kind">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {KINDS.map((k) => (
                            <SelectItem key={k} value={k}>
                                {k}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </Field>
            <Field id="list-description" label="Description">
                <Input
                    id="list-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
            </Field>
        </FormSheet>
    );
}

function AddEntriesSheet({
    list,
    onClose,
}: {
    list: RiskList;
    onClose: () => void;
}) {
    const [values, setValues] = useState('');
    const [note, setNote] = useState('');
    const [expires, setExpires] = useState('');
    const [error, setError] = useState<unknown>(null);
    const write = useWrite(
        async () =>
            (
                await api().post(`${PATH}/${list.id}/entries`, {
                    values: values
                        .split(/[\n,]/)
                        .map((v) => v.trim())
                        .filter(Boolean),
                    note: note || null,
                    expires_at: expires
                        ? new Date(expires).toISOString()
                        : null,
                })
            ).data,
        [PATH],
    );
    return (
        <FormSheet
            open
            onOpenChange={(o) => (o ? null : onClose())}
            size="sm"
            title={`Add to ${list.name}`}
            submitLabel="Add entries"
            busy={write.isPending}
            submitDisabled={values.trim() === ''}
            dirty
            error={error}
            data-testid="risk-list-entries-form"
            onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                write.mutate(undefined, {
                    onSuccess: onClose,
                    onError: setError,
                });
            }}
        >
            <Field
                id="entries-values"
                label={`Values (${list.kind})`}
                hint="One per line or comma separated"
            >
                <Textarea
                    id="entries-values"
                    rows={6}
                    value={values}
                    onChange={(e) => setValues(e.target.value)}
                />
            </Field>
            <Field id="entries-note" label="Note">
                <Input
                    id="entries-note"
                    maxLength={500}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                />
            </Field>
            <Field id="entries-expires" label="Expires" hint="Empty: never">
                <DateField
                    id="entries-expires"
                    value={expires || null}
                    onChange={(d) => setExpires(d ?? '')}
                />
            </Field>
        </FormSheet>
    );
}

function ListCard({ list }: { list: RiskList }) {
    const can = useAuthStore((s) => s.can);
    const [open, setOpen] = useState(false);
    const [adding, setAdding] = useState(false);
    const detail = useOne<ListDetail>(open ? `${PATH}/${list.id}` : null);
    const remove = useWrite(
        async (entry: number) =>
            (await api().delete(`${PATH}/${list.id}/entries/${entry}`)).data,
        [PATH],
    );
    const entries = detail.data?.data.entries ?? [];
    return (
        <Card data-testid={`risk-list-${list.name}`}>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">
                    {list.name} <Badge variant="outline">{list.kind}</Badge>{' '}
                    <Badge variant="outline">{list.managed_by}</Badge>{' '}
                    <span className="text-fg-2 text-sm font-normal">
                        {list.entries_count} entries
                    </span>
                </CardTitle>
                <div className="flex flex-wrap gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? 'Hide entries' : 'Show entries'}
                    </Button>
                    {list.editable && can(P.riskListsManage) ? (
                        <Button size="sm" onClick={() => setAdding(true)}>
                            Add entries
                        </Button>
                    ) : null}
                </div>
            </CardHeader>
            {list.description ? (
                <CardContent className="text-fg-2 pt-0 text-sm">
                    {list.description}
                </CardContent>
            ) : null}
            {open ? (
                <CardContent className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Value</TableHead>
                                <TableHead>Note</TableHead>
                                <TableHead>Added</TableHead>
                                <TableHead>Expires</TableHead>
                                <TableHead />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {entries.slice(0, 500).map((e) => (
                                <TableRow
                                    key={e.id}
                                    data-expired={String(e.expired)}
                                >
                                    <TableCell className="font-mono text-xs">
                                        {e.value}
                                    </TableCell>
                                    <TableCell>{e.note ?? '—'}</TableCell>
                                    <TableCell>
                                        {e.added_by} ·{' '}
                                        <DateTime
                                            value={e.added_at}
                                            format="date"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {e.expires_at ? (
                                            <DateTime
                                                value={e.expires_at}
                                                format="date"
                                            />
                                        ) : (
                                            'never'
                                        )}
                                        {e.expired ? ' (expired)' : ''}
                                    </TableCell>
                                    <TableCell>
                                        {list.editable &&
                                        can(P.riskListsManage) ? (
                                            <RowActions
                                                name={e.value}
                                                items={[
                                                    {
                                                        label: 'Remove',
                                                        destructive: true,
                                                        onSelect: () =>
                                                            remove.mutate(e.id),
                                                    },
                                                ]}
                                            />
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {entries.length > 500 ? (
                        <p className="text-fg-2 mt-2 text-xs">
                            First 500 of {entries.length}.
                        </p>
                    ) : null}
                </CardContent>
            ) : null}
            {adding ? (
                <AddEntriesSheet list={list} onClose={() => setAdding(false)} />
            ) : null}
        </Card>
    );
}

/** BM-125 decisions 21, 46: the cross-tenant shared negative list: the tenant's switch, the platform's counts. */
function SharedListCard() {
    const can = useAuthStore((s) => s.can);
    const status = useOne<RiskSharedList>('/staff/risk/shared-list');
    const s = status.data?.data;
    const write = useWrite(
        async (enabled: boolean) =>
            (
                await api().patch<RiskSharedList>('/staff/risk/shared-list', {
                    enabled,
                })
            ).data,
        ['/staff/risk/shared-list'],
    );
    return (
        <Card data-testid="risk-shared-list">
            <CardHeader>
                <CardTitle className="text-base">
                    Shared negative list
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
                <p className="text-fg-2 m-0">
                    Tenants that opt in share the keys they decline and flag or
                    confirm as fraud, hashed under one platform key. A key on
                    the list holds the payment for review (RT-57); the case says
                    "shared negative list", never which tenant put it there. One
                    switch covers contributing and reading.
                </p>
                {s && !s.platform ? (
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <p className="m-0 font-medium">
                                Share and read negative data
                            </p>
                            <p className="text-fg-2 m-0 text-xs">
                                {s.enabled
                                    ? 'On: your flags and confirmed fraud feed the list, and your checks read it.'
                                    : 'Off: nothing leaves this tenant and nothing is read.'}
                            </p>
                        </div>
                        <Switch
                            aria-label="Share and read negative data"
                            checked={s.enabled}
                            disabled={
                                !can(P.riskProfilesManage) || write.isPending
                            }
                            onCheckedChange={(v) => write.mutate(v)}
                        />
                    </div>
                ) : null}
                {write.error ? (
                    <p className="text-destructive m-0">
                        {apiErrorMessage(write.error)}
                    </p>
                ) : null}
                {s?.platform ? (
                    (s.counts ?? []).length ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Kind</TableHead>
                                    <TableHead className="text-right">
                                        Entries
                                    </TableHead>
                                    <TableHead className="text-right">
                                        Contributing tenants
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(s.counts ?? []).map((c) => (
                                    <TableRow key={c.kind}>
                                        <TableCell>{c.kind}</TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {c.entries}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {c.contributors ?? '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <p className="text-fg-2 m-0">No entries yet.</p>
                    )
                ) : null}
            </CardContent>
        </Card>
    );
}

export function RiskListsPage() {
    const can = useAuthStore((s) => s.can);
    const lists = useOne<RiskList[]>(PATH);
    const [creating, setCreating] = useState(false);
    return (
        <div>
            <PageHeader
                title="Risk lists"
                meta="The lists rules read with in_list: the platform's seeded and fed ones (read-only on a tenant board) and your own."
                primaryAction={
                    can(P.riskListsManage) ? (
                        <Button onClick={() => setCreating(true)}>
                            New list
                        </Button>
                    ) : undefined
                }
            />
            <Tabs defaultValue="lists">
                <TabsList>
                    <TabsTrigger value="lists">Lists</TabsTrigger>
                    <TabsTrigger value="shared">Shared list</TabsTrigger>
                </TabsList>
                <TabsContent value="lists" className="pt-2">
                    <div className="space-y-4" data-testid="risk-lists">
                        {(lists.data?.data ?? []).map((l) => (
                            <ListCard
                                key={`${l.tenant_id ?? 'platform'}-${l.name}`}
                                list={l}
                            />
                        ))}
                    </div>
                </TabsContent>
                <TabsContent value="shared" className="pt-2">
                    <SharedListCard />
                </TabsContent>
            </Tabs>
            {creating ? (
                <NewListSheet onClose={() => setCreating(false)} />
            ) : null}
        </div>
    );
}
